import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { playerSchedulePlayWindowSchema } from './playback.js';
import { dayPartCodeSchema } from './preset-showcase.js';
import { ianaTimezoneSchema } from './venue.js';
/**
 * Player discovery contracts — venue-category-discovery arch §3.4.
 *
 * DELIBERATELY NOT IN `playback.ts`. Every player schema is `.strict()`, which
 * rejects UNKNOWN keys, so a field added to `GET /player/me`,
 * `GET /player/schedule` or `GET /player/presets` is rejected by the bundle
 * that is ALREADY RUNNING on a venue tablet — and the player polls for a
 * service-worker update once an hour (`SW_UPDATE_INTERVAL_MS`). ADR-038 draws
 * the conclusion: new player data ships on NEW endpoints, and the three hot
 * reads are frozen. Keeping the new schemas in their own module is the
 * mechanical half of that rule — the frozen file is never open in the editor
 * while the new shapes are being written.
 *
 * `apps/api/tests/player/adr-038-frozen-player-reads.test.ts` is the other
 * half: it fails if any frozen schema gains, loses or renames a key.
 */
/**
 * `GET /player/venue-categories` — one venue category (D-1: the existing
 * `internal.venue_formats` dictionary, renamed in the UI only).
 */
export const playerVenueCategorySchema = z
    .object({
    id: uuidSchema,
    /** Stable key the FE maps to an icon — never a translated label. */
    code: z.string(),
    name: z.string(),
    /** Matches the venue's `format_id`. */
    is_current: z.boolean(),
})
    .strict();
/**
 * THE VENUE'S OWN CATEGORY IS ALWAYS IN THIS LIST (Gate 3 review amendment).
 * `is_active` is a curation knob, not a soft-delete: turning it off hides a
 * category from the venue-create dropdown while venues keep pointing at it. An
 * "active only" list would therefore drop the venue's OWN category the moment
 * a curator deactivates it, and the picker would render a grid with nothing
 * selected — the client's category silently vanishing with no way to tell what
 * is set. The server query is `is_active AND deleted_at IS NULL` OR
 * `id = venue.format_id`, so at most one extra row appears, only for the venue
 * it belongs to, and only while it is still selected.
 */
export const playerVenueCategoryListSchema = z
    .object({
    /** `'other'` («Прочее») sorted last. */
    items: z.array(playerVenueCategorySchema),
})
    .strict();
/**
 * One day of the venue's working hours.
 *
 * The player always gets EXACTLY 7 rows with an explicit `is_open`, unlike the
 * sparse admin shape (`{dow,start,end}[]`, ≤14 rows): «no row for Wednesday»
 * and «closed on Wednesday» are the same byte on the admin wire and two
 * different sentences in the UI. The service densifies on read and sparsifies
 * on write.
 *
 * `crosses_midnight` is server-computed (`end <= start`) and rendered verbatim
 * — the FE derives nothing about time, the doctrine of ADR-030's `play_window`.
 */
export const playerWorkingHoursDaySchema = z
    .object({
    dow: z.number().int().min(0).max(6),
    is_open: z.boolean(),
    start: localTimeSchema.nullable(),
    end: localTimeSchema.nullable(),
    crosses_midnight: z.boolean(),
})
    .strict();
/** The category's typical opening hours. `close < open` = shuts the next day. */
export const playerVenueTypicalHoursSchema = z
    .object({ open: localTimeSchema, close: localTimeSchema })
    .strict();
/** `GET /player/venue/profile` — all tariffs (D-10). */
export const playerVenueProfileSchema = z
    .object({
    venue_id: uuidSchema,
    category: playerVenueCategorySchema.nullable(),
    timezone: z.string(),
    /** «сейчас у вас HH:MM» — computed by the server. */
    timezone_now_local: localTimeSchema,
    working_hours: z.array(playerWorkingHoursDaySchema).length(7),
    /**
     * The venue has actually SET its hours. `working_hours` is always seven
     * dense rows, so «never configured» and «closed all week» look identical on
     * the wire — and the editor opened every day as «выходной» for a venue that
     * had simply not been asked yet (BUG-291). The distinction lives on the
     * server, which alone can see the NULL column.
     */
    working_hours_configured: z.boolean(),
    /**
     * The category's typical opening hours — what the editor offers a venue
     * that has never set its own (BUG-294). `close < open` means the venue
     * shuts the next day (bar 16:00→02:00); `null` when the category is unset
     * or carries no opinion.
     */
    typical_hours: playerVenueTypicalHoursSchema.nullable(),
    /** ≥1 daily anchor. */
    has_schedule: z.boolean(),
    /** The tariff allows auto-scheduling (Standard+, D-10). */
    can_auto_schedule: z.boolean(),
    /**
     * US-8 / design §4.5, narrowed to the CATEGORY by D-16: `true` when the
     * category was changed after the last daily-schedule write AND a schedule
     * exists. A working-hours change deliberately does not raise it.
     */
    category_changed_since_schedule: z.boolean(),
    /**
     * Dismissal key for «Не сейчас» (§2.1.1). The player remembers the value it
     * dismissed and re-shows the banner only when a NEWER timestamp arrives, so
     * no server-side dismissal state is needed — and on a second device the
     * banner shows once too, which is the honest outcome.
     */
    format_changed_at: isoDateTimeSchema.nullable(),
})
    .strict();
/**
 * `PATCH /player/venue/profile`. Response is the fresh
 * `playerVenueProfileSchema`.
 *
 * `timezone_confirmed` is required by the SERVER when `timezone` changes
 * (409 `TIMEZONE_CONFIRMATION_REQUIRED`), not by zod: the dialog the flag
 * confirms is driven by `GET /player/venue/timezone-preview`, and a schema-level
 * `refine` would make the two fields look like one atomic edit when they are a
 * two-step conversation.
 */
export const playerVenueProfilePatchSchema = z
    .object({
    format_id: uuidSchema.nullable().optional(),
    timezone: ianaTimezoneSchema.optional(),
    working_hours: z.array(playerWorkingHoursDaySchema).length(7).optional(),
    timezone_confirmed: z.boolean().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/** `GET /player/venue/timezone-preview` query string. */
export const playerTimezonePreviewQuerySchema = z
    .object({
    timezone: ianaTimezoneSchema,
})
    .strict();
/**
 * One side of the timezone confirmation dialog (design §3.5.3 / §19.4): the
 * dialog must show a CONSEQUENCE, not arithmetic.
 *
 * Both sides are computed by the SERVER through the same path `/player/schedule`
 * uses (`computeVenueLocalNow` → `resolveActiveSlot` → the anchor's persisted
 * `part`). The FE renders «Играет „День"» / «Будет играть „Вечер"» verbatim and
 * never derives a part from a clock — the BUG-207/214/215/226 class.
 *
 * `active_part: null` is the documented degradation (no schedule, or nothing
 * resolves): the part lines are simply not rendered. The server never guesses a
 * part it cannot resolve.
 */
export const playerTimezoneSideSchema = z
    .object({
    timezone: z.string(),
    local_time: localTimeSchema,
    active_part: dayPartCodeSchema.nullable(),
})
    .strict();
export const playerTimezonePreviewSchema = z
    .object({
    current: playerTimezoneSideSchema,
    next: playerTimezoneSideSchema,
    changes_active_part: z.boolean(),
})
    .strict();
/**
 * One selectable facet value. `count` is `>0` by construction — an empty facet
 * is ABSENT rather than present-and-zero, so the client never renders a chip
 * that leads to an empty list.
 */
export const playerFacetOptionSchema = z
    .object({
    /** `tag_id`, or `venue_format_id` in the category group. */
    value: uuidSchema,
    /** `'morning'|'day'|'evening'` for day parts, the format code for
     *  categories, `null` for an ordinary tag. */
    code: z.string().nullable(),
    /** `coalesce(client_name, name)` — resolved server-side (D-12). */
    label: z.string(),
    count: z.number().int().positive(),
    /** `true` on the venue's own category option. */
    is_venue_default: z.boolean(),
})
    .strict();
/**
 * `kind` encodes ADR-031's selection rule on the UI side: the venue category is
 * `single` (one category at a time), tag groups are `multi` (OR inside the
 * group). Across groups the server ANDs — see `playerPresetFacetedQuerySchema`.
 */
export const playerFacetGroupSchema = z
    .object({
    /** `'venue_category' | 'day_part' | 'mood' | 'energy' | 'genre'`. */
    code: z.string(),
    label: z.string(),
    kind: z.enum(['single', 'multi']),
    options: z.array(playerFacetOptionSchema).min(1),
})
    .strict();
/** `GET /player/presets/facets`. */
export const playerFacetsResponseSchema = z
    .object({
    groups: z.array(playerFacetGroupSchema),
    /** Echo, so the FE can pre-select «Для моей категории» (D-20). */
    venue_format_id: uuidSchema.nullable(),
    total_presets: z.number().int().nonnegative(),
})
    .strict();
/**
 * `GET /player/presets/search` query string.
 *
 * ADR-031 SEMANTICS, WHICH THIS SHAPE MUST NOT LOSE: tag ids are OR-ed WITHIN a
 * tag category and AND-ed ACROSS categories. The grouping is not on the wire
 * because the server resolves each `tag_id` to its `category_id` — the client
 * cannot express «AND inside a group» and must not be able to. A flat AND over
 * all ids would silently return nothing for the most natural two-chip
 * selection, and that is the contract BUG-240 fixed.
 *
 * `tag_ids` travels as ONE comma-separated string rather than a repeated key:
 * Express does not coerce repeated unknown query keys consistently, and the
 * project already rejected the flat fan-out for the same reason (`custom_fields`,
 * Wave 2.6). The service splits, validates each as a uuid
 * (400 `PLAYER_FACET_INVALID_TAG_ID`) and caps at 30.
 *
 * Pagination is the SAME keyset axis and the same opaque base64 cursor as
 * `GET /player/presets` — `decodeCursor` is reused verbatim, no new cursor
 * semantics.
 */
export const playerPresetFacetedQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(120).optional(),
    venue_format_id: uuidSchema.optional(),
    tag_ids: z.string().max(1024).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().max(1024).optional(),
})
    .strict();
/**
 * The richer sibling of the frozen `playerPresetItemSchema` (ADR-038 rule 2).
 * `GET /player/presets` keeps returning the narrow shape unchanged, so a stale
 * kiosk bundle keeps working; the faceted screen calls the new route and gets
 * the extra fields. Two schemas is the price, a working kiosk is what it buys.
 */
export const playerPresetFacetedItemSchema = z
    .object({
    preset_id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    tier_access: z.enum(['lite', 'standard', 'pro']),
    is_active_override: z.boolean(),
    is_tier_default: z.boolean(),
    cover_url: z.string().nullable(),
    /** The preset is linked to the venue's own category. */
    matches_venue_category: z.boolean(),
    day_parts: z.array(dayPartCodeSchema),
    /** Showcase chips, already resolved to tenant labels. */
    labels: z
        .array(z.object({ category_code: z.string(), label: z.string() }).strict())
        .max(6),
})
    .strict();
export const playerPresetFacetedResponseSchema = z
    .object({
    items: z.array(playerPresetFacetedItemSchema),
    /** Cursor-free count — the «N подборок» header of the faceted screen. */
    total: z.number().int().nonnegative(),
    next_cursor: z.string().nullable(),
})
    .strict();
/**
 * One proposed part of `POST /player/schedule/auto/preview`.
 *
 * NO `preset_cover_url` (design DD-7). Preset artwork is a presigned GET with a
 * 10-minute TTL and this modal can stay open longer than that; a URL that
 * quietly rots inside an open dialog is the BUG-112/123 shape. The preview is a
 * compact text list by design, so the field is not merely unused — it is a
 * liability.
 */
export const playerAutoSchedulePartSchema = z
    .object({
    part: dayPartCodeSchema,
    start_local_time: localTimeSchema,
    /** ADR-030 shape, reused from `playback.ts` — reading a frozen schema is
     *  always safe; only adding fields TO one is forbidden (ADR-038 rule 3). */
    play_window: playerSchedulePlayWindowSchema,
    items: z
        .array(z
        .object({
        preset_id: uuidSchema,
        preset_name: z.string(),
        /** `null` === «до конца слота», legal on the last item only. */
        duration_minutes: z.number().int().nullable(),
    })
        .strict())
        .max(3),
    /**
     * `empty_reason` is deliberately NOT widened for the category template
     * (DV-4). A part whose template positions have all rotted falls through to
     * the ranking (§4.3 step 0) and reaches the operator as an ordinary
     * `algorithm` part — with `no_candidates` if the ranking also finds
     * nothing. The client is not told that its category's template is broken:
     * that is the curator's problem, surfaced on the curator's surface and in
     * the metrics.
     */
    empty_reason: z.enum(['no_candidates']).nullable(),
    /**
     * Which source filled this part (arch §3.5, design §19.18). A VALUE, never
     * a client inference — the same rule `boundary_source` follows: a part
     * sourced from the category template and a part the algorithm happened to
     * fill with the same presets are indistinguishable from the outside, so
     * only the server can say which.
     *
     * REQUIRED, not optional: every part names its source. Free under ADR-038
     * as sharpened in arch §3.1 — `POST /player/schedule/auto/preview` ships in
     * this release, so no deployed bundle has ever compiled this schema and
     * there is no stale reader to break. From v1.2.0 on it is frozen on the
     * same terms as the other player reads.
     */
    source: z.enum(['category_template', 'algorithm']),
})
    .strict();
/** The working-hours window the boundaries were derived from, echoed for
 *  «(Пн–Пт 08:00–23:00)». */
export const playerAutoScheduleWindowSchema = z
    .object({
    dows: z.array(z.number().int().min(0).max(6)).min(1),
    start: localTimeSchema,
    end: localTimeSchema,
})
    .strict();
export const playerAutoSchedulePreviewSchema = z
    .object({
    /** sha256 of the canonical proposal; `apply` refuses a stale one. */
    proposal_hash: z.string(),
    /**
     * ONE enum of exactly three values (design §19.5). The «fallback after
     * conflict» branch (PRD §7.3 step 1.4) is indistinguishable from «format
     * defaults» from the outside, so it can only come from the server.
     */
    boundary_source: z.enum([
        'working_hours',
        'format_defaults',
        'fallback_after_conflict',
    ]),
    working_window: playerAutoScheduleWindowSchema.nullable(),
    category: playerVenueCategorySchema,
    /** ≥1 daily anchor today → the button reads «Заменить расписание». */
    replaces_existing: z.boolean(),
    /** D-15 — apply is refused when true, independently of the disabled
     *  button: a disabled control must never be the only thing standing
     *  between an operator and an erased schedule. */
    all_parts_empty: z.boolean(),
    active_override_preset_name: z.string().nullable(),
    parts: z.array(playerAutoSchedulePartSchema).length(3),
    warnings: z.array(z.string()),
})
    .strict();
/**
 * `POST /player/schedule/auto/apply`. The response is the EXISTING
 * `PlayerScheduleResponse`, so the screen re-renders from one known contract
 * instead of a bespoke apply payload.
 */
export const playerAutoScheduleApplyRequestSchema = z
    .object({
    proposal_hash: z.string().min(16).max(128),
})
    .strict();
//# sourceMappingURL=player-discovery.js.map